import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Plus, ArrowLeft, ArrowRight, Check } from 'lucide-react';

import api from '../../services/api';
import { toEmbedUrl } from '../my-trainings/player/EmbedPlayer';
import {
  getTraining,
  createTraining,
  updateTraining,
  listTrainingContent,
  addTrainingContent,
  removeTrainingContent,
  getTrainingQuizConfig,
  upsertTrainingQuizConfig,
  listTrainingQuizQuestions,
  addTrainingQuizQuestion,
  deleteTrainingQuizQuestion,
  submitTrainingForApproval,
  getTrainingCategories,
  getTrainingApprovalSteps,
  getCertificateTemplates,
} from '../../services/trainingService';
import { formatDate } from '../../utils/Utils';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import PictureUpload from '../../components/ui/PictureUpload';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const Container = styled.div`padding: 24px; max-width: 960px; margin: 0 auto;`;

const StepIndicator = styled.div`
  display: flex;
  gap: 4px;
  margin: 16px 0 24px;
`;

const StepDot = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: ${({ $done, $active, theme }) =>
    $done ? theme.colors.primary.main :
    $active ? theme.colors.primary.light || '#60A5FA' :
    '#E0E4E8'};
`;

const StepLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const SectionTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '1fr 1fr'};
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  margin-bottom: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: #E0E7FF;
  color: #3730A3;
  text-transform: uppercase;
`;

const IconButton = styled.button`
  background: none;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #ef4444;
  &:hover { background: #fef2f2; }
`;

const QuestionItem = styled.div`
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 10px;
`;

const QuestionText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ $correct }) => $correct ? '#16a34a' : '#64748b'};
  padding: 2px 0;
`;

const ApproverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #F8FAFC;
  border-radius: 6px;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

const ApproverIndex = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  min-width: 18px;
`;

// Surfaced at the top of the wizard so the creator immediately sees why the
// reviewer sent the training back. Yellow for revision_requested, red for
// rejected. Hidden once the creator resubmits (Status returns to pending_approval).
const RevisionBanner = styled.div`
  background: ${({ $kind }) => $kind === 'rejected' ? '#FEE2E2' : '#FEF3C7'};
  border-left: 4px solid ${({ $kind }) => $kind === 'rejected' ? '#DC2626' : '#F59E0B'};
  padding: 14px 16px;
  border-radius: 0 6px 6px 0;
  margin-bottom: 16px;
  font-family: 'Nunito Sans', sans-serif;
`;

const RevisionTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: ${({ $kind }) => $kind === 'rejected' ? '#991B1B' : '#92400E'};
  margin-bottom: 6px;
`;

const RevisionMeta = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 6px;
`;

const RevisionComment = styled.div`
  font-size: 13px;
  color: #1f2937;
  line-height: 1.5;
  white-space: pre-wrap;
`;

// Compact horizontal approval-flow strip shown to the creator while the
// training is pending_approval. Mirrors the reviewer's chain view but is
// read-only for the creator.
const FlowCard = styled.div`
  background: #fff;
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const FlowTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const FlowSteps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${({ $current, $decision }) =>
    $current ? '#DBEAFE' :
    $decision === 'approved' ? '#DCFCE7' :
    $decision === 'revision_requested' ? '#FEF3C7' :
    $decision === 'rejected' ? '#FEE2E2' : '#F1F5F9'};
  color: ${({ $current, $decision }) =>
    $current ? '#1E40AF' :
    $decision === 'approved' ? '#166534' :
    $decision === 'revision_requested' ? '#92400E' :
    $decision === 'rejected' ? '#991B1B' : '#64748B'};
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${({ $current }) => $current ? '#0291EF' : 'transparent'};
`;

const FlowMetaRow = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
  flex-wrap: wrap;
`;

// Upload pictureUpload-style file to R2 via presign, return the file's URL.
// `purpose` tells the server which visibility bucket this belongs to (see
// backend StoragePurposes) — the client never chooses public/private itself.
async function uploadViaPresign(file, purpose) {
  if (!file || !(file instanceof File)) return null;
  const presign = await api.post(
    `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type)}&purpose=${encodeURIComponent(purpose)}`,
  );
  const { upload_url, file_url } = presign.data || {};
  if (!upload_url) return null;
  await axios.put(upload_url, file, { headers: { 'Content-Type': file.type } });
  return file_url || null;
}

export default function TrainingWizardPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { id: idFromRoute } = useParams();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [trainingId, setTrainingId] = useState(idFromRoute || null);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [loading, setLoading] = useState(!!idFromRoute);
  const [saving, setSaving] = useState(false);
  const [formDirty, setFormDirty] = useState(!idFromRoute); // new trainings are always dirty
  const readOnly = trainingStatus === 'pending_approval';

  // Step 1 state
  const [categories, setCategories] = useState([]);
  const updateForm = useCallback((patch) => { setFormDirty(true); setForm((prev) => ({ ...prev, ...patch })); }, []);
  const [form, setForm] = useState({
    title: '',
    shortDescription: '',
    categoryId: null,
    coverImageUrl: '',
    tags: [],
    durationMinutes: 30,
    isMandatory: false,
    hasQuiz: false,
    videoCompletionThreshold: 90,
    disableFastForward: false,
    certificateValidityMonths: null,
    certificateTemplateId: null,
  });
  const [coverFile, setCoverFile] = useState(null);

  // Step 2 state
  const [contentItems, setContentItems] = useState([]);
  const [newContentType, setNewContentType] = useState('video');
  const [newContentFile, setNewContentFile] = useState(null);
  const [newEmbedUrl, setNewEmbedUrl] = useState('');
  const [uploadingContent, setUploadingContent] = useState(false);

  // Step 3 state
  const [quizConfig, setQuizConfig] = useState({
    durationMinutes: 45,
    questionCount: 10,
    passingScore: 70,
    maxAttempts: 3,
    shuffleQuestions: true,
    shuffleOptions: true,
    showAnswersAfter: true,
    allowBacktrack: true,
    antiCheatEnabled: true,
  });
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState(() => emptyQuestion());

  // Step 4 state
  const [approvers, setApprovers] = useState([]);
  const [approverMap, setApproverMap] = useState({});

  // Surface revision feedback + approval chain to the creator. Loaded once on
  // mount; refreshed implicitly when the wizard reloads after a navigation.
  const [trainingMeta, setTrainingMeta] = useState(null);
  const [approvalSteps, setApprovalSteps] = useState([]);

  // --- Initial load ---

  useEffect(() => {
    getTrainingCategories({ pageSize: 200 })
      .then((res) => setCategories(res.data?.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!idFromRoute) return;
    setLoading(true);
    Promise.all([
      getTraining(idFromRoute).then((res) => res.data?.data),
      listTrainingContent(idFromRoute).then((res) => res.data?.data || []),
      getTrainingQuizConfig(idFromRoute).then((res) => res.data?.data).catch(() => null),
      listTrainingQuizQuestions(idFromRoute).then((res) => res.data?.data || []).catch(() => []),
      getTrainingApprovalSteps(idFromRoute).then((res) => res.data?.data || []).catch(() => []),
    ]).then(([tr, items, quiz, qs, steps]) => {
      if (tr) {
        if (tr.status !== 'draft' && tr.status !== 'pending_approval') {
          history.push('/trainings');
          return;
        }
        setTrainingStatus(tr.status);
        setTrainingMeta(tr);
        setApprovalSteps(steps);
        setForm({
          title: tr.title || '',
          shortDescription: tr.shortDescription || '',
          categoryId: tr.categoryId,
          coverImageUrl: tr.coverImageUrl || '',
          tags: tr.tags || [],
          durationMinutes: tr.durationMinutes,
          isMandatory: !!tr.isMandatory,
          hasQuiz: !!tr.hasQuiz,
          videoCompletionThreshold: tr.videoCompletionThreshold ?? 90,
          disableFastForward: !!tr.disableFastForward,
          certificateValidityMonths: tr.certificateValidityMonths ?? null,
          certificateTemplateId: tr.certificateTemplateId ?? null,
        });
      }
      setContentItems(items);
      if (quiz) setQuizConfig((prev) => ({ ...prev, ...quiz }));
      setQuestions(qs);
    }).finally(() => setLoading(false));
  }, [idFromRoute]);

  // --- Step 1 ---

  const canAdvanceFrom1 = !!(form.title && form.shortDescription && form.categoryId && form.durationMinutes);

  const saveDraft = useCallback(async () => {
    setSaving(true);
    try {
      let coverImageUrl = form.coverImageUrl;
      if (coverFile instanceof File) {
        const uploaded = await uploadViaPresign(coverFile, 'trainingCover');
        if (uploaded) coverImageUrl = uploaded;
      }
      const payload = { ...form, coverImageUrl };
      if (trainingId) {
        const res = await updateTraining(trainingId, payload);
        return res.data?.data;
      }
      const res = await createTraining(payload);
      const created = res.data?.data;
      if (created?.id) {
        setTrainingId(created.id);
        // Update URL without triggering React Router re-render.
        window.history.replaceState(null, '', `/trainings/${created.id}/edit`);
      }
      return created;
    } finally {
      setSaving(false);
    }
  }, [form, coverFile, trainingId, history]);

  const handleNextFrom1 = async () => {
    if (!canAdvanceFrom1) return;
    if (!trainingId) {
      // New training — must save first
      const saved = await saveDraft();
      if (saved) { setFormDirty(false); setStep(2); }
    } else if (formDirty) {
      // Existing training with changes — try to save
      try {
        await saveDraft();
        setFormDirty(false);
      } catch { /* non-draft or other error, skip */ }
      setStep(2);
    } else {
      // Existing training, no changes — just navigate
      setStep(2);
    }
  };

  // --- Step 2: content ---

  const addContentItem = useCallback(async (fileOverride) => {
    if (!trainingId) return;
    setUploadingContent(true);
    try {
      const type = newContentType;
      let url = '';
      let fileName;
      let sizeBytes;
      if (type === 'youtube' || type === 'vimeo') {
        if (!newEmbedUrl.trim()) return;
        url = toEmbedUrl(newEmbedUrl.trim());
      } else {
        const file = fileOverride instanceof File ? fileOverride : newContentFile;
        if (!(file instanceof File)) return;
        const uploaded = await uploadViaPresign(file, 'trainingContent');
        if (!uploaded) return;
        url = uploaded;
        fileName = file.name;
        sizeBytes = file.size;
      }
      await addTrainingContent(trainingId, {
        contentType: type,
        url,
        orderIndex: contentItems.length,
        originalFileName: fileName,
        sizeBytes,
      });
      const fresh = await listTrainingContent(trainingId);
      setContentItems(fresh.data?.data || []);
      setNewContentFile(null);
      setNewEmbedUrl('');
    } finally {
      setUploadingContent(false);
    }
  }, [trainingId, newContentType, newContentFile, newEmbedUrl, contentItems.length]);

  const handleRemoveContent = useCallback(async (itemId) => {
    if (!trainingId) return;
    await removeTrainingContent(trainingId, itemId);
    const fresh = await listTrainingContent(trainingId);
    setContentItems(fresh.data?.data || []);
  }, [trainingId]);

  // --- Step 3: quiz ---

  const saveQuizConfig = useCallback(async () => {
    if (!trainingId) return null;
    const res = await upsertTrainingQuizConfig(trainingId, quizConfig);
    return res.data?.data;
  }, [trainingId, quizConfig]);

  const handleNextFrom3 = async () => {
    if (form.hasQuiz) {
      try { await saveQuizConfig(); } catch { /* non-draft, skip */ }
    }
    setStep(4);
  };

  const addQuestion = useCallback(async () => {
    if (!trainingId) return;
    if (!form.hasQuiz) return;
    // Ensure config is saved first so the quiz row exists.
    if (!(await _quizExists())) {
      await saveQuizConfig();
    }
    const normalised = normaliseQuestion(newQuestion);
    await addTrainingQuizQuestion(trainingId, normalised);
    const fresh = await listTrainingQuizQuestions(trainingId);
    setQuestions(fresh.data?.data || []);
    setNewQuestion(emptyQuestion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingId, form.hasQuiz, newQuestion, saveQuizConfig]);

  const _quizExists = async () => {
    if (!trainingId) return false;
    const res = await getTrainingQuizConfig(trainingId).catch(() => null);
    return !!res?.data?.data;
  };

  const handleDeleteQuestion = useCallback(async (qid) => {
    if (!trainingId) return;
    await deleteTrainingQuizQuestion(trainingId, qid);
    setQuestions((prev) => prev.filter((q) => q.id !== qid));
  }, [trainingId]);

  // --- Step 4: submit for approval ---

  const [lastSearchResults, setLastSearchResults] = useState([]);

  const handleApproversChange = useCallback((ids) => {
    setApprovers(ids);
    // Capture details for newly added IDs
    setApproverMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (!next[id]) {
          const found = lastSearchResults.find((r) => r.id === id);
          if (found) next[id] = found;
        }
      });
      return next;
    });
  }, [lastSearchResults]);

  const canSubmit = approvers.length >= 1 && approvers.length <= 3;

  const handleSubmit = useCallback(async () => {
    if (!trainingId || !canSubmit) return;
    setSaving(true);
    try {
      await submitTrainingForApproval(trainingId, approvers);
      history.push('/trainings');
    } finally {
      setSaving(false);
    }
  }, [trainingId, canSubmit, approvers, history]);

  const handleSaveDraftOnly = useCallback(async () => {
    setSaving(true);
    try {
      if (form.hasQuiz) await saveQuizConfig();
      history.push('/trainings');
    } finally {
      setSaving(false);
    }
  }, [form.hasQuiz, saveQuizConfig, history]);

  // --- Employee search for approvers ---

  const searchEmployees = useCallback(async (term) => {
    const params = term ? { 'fullName[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const currentUserId = user?.id || user?.userId;
    const results = (res.data?.data?.data || [])
      .filter((e) => (e.userId || e.id) !== currentUserId)
      .map((e) => ({
        id: e.userId || e.id,
        name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
        titleName: e.titleName || '',
      }));
    setLastSearchResults(results);
    return results;
  }, [user]);

  // --- Render ---

  const steps = [1, 2, 3, 4];
  const stepLabels = [
    t('training.wizardStep1'),
    t('training.wizardStep2'),
    t('training.wizardStep3'),
    t('training.wizardStep4'),
  ];

  if (loading) return <Container>{t('common.loading')}</Container>;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('training.pageTitle'), path: '/trainings' },
        { label: trainingId ? (form.title || t('training.edit')) : t('training.new') },
      ]} />

      <StepLabel>
        {stepLabels.map((l, i) => (
          <span key={i} style={{ fontWeight: step === i + 1 ? 700 : 400 }}>{i + 1}. {l}</span>
        ))}
      </StepLabel>
      <StepIndicator>
        {steps.map((s) => (
          <StepDot key={s} $done={step > s} $active={step === s} />
        ))}
      </StepIndicator>

      {trainingMeta?.lastReviewerComment && trainingStatus === 'draft' && (
        <RevisionBanner $kind={trainingMeta.lastReviewerDecision === 'rejected' ? 'rejected' : 'revision'}>
          <RevisionTitle $kind={trainingMeta.lastReviewerDecision === 'rejected' ? 'rejected' : 'revision'}>
            {trainingMeta.lastReviewerDecision === 'rejected'
              ? t('training.rejectedBanner')
              : t('training.revisionBanner')}
          </RevisionTitle>
          <RevisionMeta>
            {t('training.reviewer')}: <b>{trainingMeta.lastReviewerFullName || '—'}</b>
            {trainingMeta.lastReviewerDecidedAt && (
              <> · {formatDate(trainingMeta.lastReviewerDecidedAt, i18n.language)}</>
            )}
          </RevisionMeta>
          <RevisionComment>{trainingMeta.lastReviewerComment}</RevisionComment>
        </RevisionBanner>
      )}

      {trainingStatus === 'pending_approval' && approvalSteps.length > 0 && (
        <FlowCard>
          <FlowTitle>{t('training.approvalFlow')}</FlowTitle>
          <FlowSteps>
            {approvalSteps.map((s) => (
              <FlowStep key={s.id} $current={s.isCurrent} $decision={s.decision}>
                <span>{s.orderIndex + 1}.</span>
                <span>{s.approverFullName || s.approverUserId}</span>
                {s.isCurrent && <span>· {t('training.waitingApproval')}</span>}
                {s.decision === 'approved' && <span>· {t('training.approved')}</span>}
                {s.decision === 'revision_requested' && <span>· {t('training.revisionRequested')}</span>}
                {s.decision === 'rejected' && <span>· {t('training.rejected')}</span>}
              </FlowStep>
            ))}
          </FlowSteps>
          <FlowMetaRow>
            {trainingMeta?.createUserFullName && (
              <span>{t('training.createdBy')}: <b>{trainingMeta.createUserFullName}</b></span>
            )}
            {trainingMeta?.currentApproverFullName && (
              <span>{t('training.waitingOn')}: <b>{trainingMeta.currentApproverFullName}</b></span>
            )}
          </FlowMetaRow>
        </FlowCard>
      )}

      <Card>
        {step === 1 && (
          <>
            <SectionTitle>{t('training.wizardStep1')}</SectionTitle>
            <FormGrid $cols="1fr">
              <TextInput
                label={t('training.titleField')}
                value={form.title}
                onChange={(e) => updateForm({ title: e.target.value })}
                maxLength={150}
                required
              />
              <TextInput
                label={t('training.shortDescription')}
                value={form.shortDescription}
                onChange={(e) => updateForm({ shortDescription: e.target.value })}
                maxLength={500}
                textarea
                required
              />
            </FormGrid>
            <FormGrid style={{ marginTop: 16 }}>
              <GenericSelect
                label={t('training.category')}
                value={form.categoryId}
                onChange={(v) => updateForm({ categoryId: v })}
                options={categories}
                idKey="id"
                labelKey="name"
                required
              />
              <TextInput
                label={t('training.durationMinutes')}
                type="number"
                value={form.durationMinutes}
                onChange={(e) => updateForm({ durationMinutes: parseInt(e.target.value, 10) || 0 })}
                required
              />
              <TextInput
                label={t('training.videoCompletionThreshold')}
                type="number"
                value={form.videoCompletionThreshold}
                onChange={(e) => updateForm({ videoCompletionThreshold: parseInt(e.target.value, 10) || 90 })}
                description={t('training.videoCompletionThresholdHint')}
              />
              <TextInput
                label={t('training.certificateValidityMonths')}
                type="number"
                value={form.certificateValidityMonths ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  updateForm({ certificateValidityMonths: Number.isFinite(v) && v > 0 ? v : null });
                }}
                description={t('training.certificateValidityMonthsHint')}
              />
              <AsyncGenericSelect
                label={t('training.certificateTemplate')}
                value={form.certificateTemplateId ?? null}
                onChange={(id) => updateForm({ certificateTemplateId: id || null })}
                onSearch={async (q) => {
                  const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
                  const res = await getCertificateTemplates(params);
                  const items = res.data?.data?.data || [];
                  return items.map((i) => ({ id: i.id, name: i.name }));
                }}
                onGetById={async (id) => {
                  if (!id) return null;
                  const res = await getCertificateTemplates({ pageSize: 200 });
                  const items = res.data?.data?.data || [];
                  const item = items.find((i) => i.id === id);
                  return item ? { id: item.id, name: item.name } : { id, name: '' };
                }}
              />
              <TextInput
                label={t('training.tags')}
                value={(form.tags || []).join(', ')}
                onChange={(e) => updateForm({ tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                description={t('training.tagsHint')}
              />
            </FormGrid>
            <FormGrid style={{ marginTop: 16 }}>
              <CheckboxInput
                label={t('training.isMandatory')}
                checked={form.isMandatory}
                onChange={(v) => updateForm({ isMandatory: v })}
              />
              <CheckboxInput
                label={t('training.hasQuiz')}
                checked={form.hasQuiz}
                onChange={(v) => updateForm({ hasQuiz: v })}
              />
              <CheckboxInput
                label={t('training.disableFastForward')}
                checked={form.disableFastForward}
                onChange={(v) => updateForm({ disableFastForward: v })}
              />
            </FormGrid>
            <div style={{ marginTop: 16 }}>
              <PictureUpload
                label={t('training.coverImage')}
                value={coverFile || form.coverImageUrl}
                onChange={setCoverFile}
                accept="image/*"
                validTypes={['image/jpeg', 'image/png', 'image/webp']}
                maxSize={5}
              />
            </div>
            <Footer>
              <Button variant="outline" onClick={() => history.goBack()}>
                {readOnly ? t('common.back') : t('common.cancel')}
              </Button>
              {readOnly ? (
                <Button onClick={() => setStep(2)}>
                  {t('common.next')} <ArrowRight size={14} style={{ marginLeft: 4 }} />
                </Button>
              ) : (
                <Button onClick={handleNextFrom1} disabled={!canAdvanceFrom1 || saving}>
                  {saving ? '...' : t('common.next')} <ArrowRight size={14} style={{ marginLeft: 4 }} />
                </Button>
              )}
            </Footer>
          </>
        )}

        {step === 2 && (
          <>
            <SectionTitle>{t('training.wizardStep2')}</SectionTitle>
            <div style={{ marginBottom: 16 }}>
              {contentItems.length === 0 && (
                <div style={{ color: '#A0A8B4', fontSize: 13 }}>{t('training.noContentYet')}</div>
              )}
              {contentItems.map((ci) => (
                <ContentRow key={ci.id}>
                  <TypeBadge>{ci.contentType}</TypeBadge>
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ci.originalFileName || ci.url}
                  </div>
                  {!readOnly && (
                    <IconButton onClick={() => handleRemoveContent(ci.id)}>
                      <Trash2 size={14} />
                    </IconButton>
                  )}
                </ContentRow>
              ))}
            </div>

            {!readOnly && (
              <div style={{ borderTop: '1px solid #E0E4E8', paddingTop: 16 }}>
                <FormGrid>
                  <GenericSelect
                    label={t('training.contentType')}
                    value={newContentType}
                    onChange={setNewContentType}
                    options={[
                      { id: 'video', name: t('training.contentVideo') },
                      { id: 'pdf', name: 'PDF' },
                      { id: 'pptx', name: 'PPTX' },
                      { id: 'image', name: t('training.contentImage') },
                      { id: 'youtube', name: 'YouTube' },
                      { id: 'vimeo', name: 'Vimeo' },
                    ]}
                    idKey="id"
                    labelKey="name"
                  />
                  {(newContentType === 'youtube' || newContentType === 'vimeo') ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          label={t('training.embedUrl')}
                          value={newEmbedUrl}
                          onChange={(e) => setNewEmbedUrl(e.target.value)}
                        />
                      </div>
                      <Button variant="ghost" onClick={addContentItem} disabled={uploadingContent || !newEmbedUrl.trim()} style={{ padding: '8px 14px', fontSize: 13, marginBottom: 1 }}>
                        <Plus size={14} />
                        {uploadingContent ? t('training.uploading') : t('common.add')}
                      </Button>
                    </div>
                  ) : (
                    <PictureUpload
                      label={uploadingContent ? t('training.uploading') : t('training.uploadFile')}
                      value={newContentFile}
                      onChange={(file) => {
                        setNewContentFile(file);
                        if (file instanceof File) addContentItem(file);
                      }}
                      accept={newContentType === 'pdf' ? '.pdf' : newContentType === 'pptx' ? '.ppt,.pptx' : newContentType === 'image' ? 'image/*' : 'video/*'}
                      maxSize={newContentType === 'video' ? 2048 : 100}
                    />
                  )}
                </FormGrid>
              </div>
            )}

            <Footer>
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft size={14} style={{ marginRight: 4 }} /> {t('common.back')}
              </Button>
              <Button onClick={() => setStep(form.hasQuiz ? 3 : 4)} disabled={contentItems.length === 0}>
                {t('common.next')} <ArrowRight size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Footer>
          </>
        )}

        {step === 3 && form.hasQuiz && (
          <>
            <SectionTitle>{t('training.wizardStep3')}</SectionTitle>
            <FormGrid>
              <TextInput label={t('training.quizDurationMinutes')} type="number" value={quizConfig.durationMinutes}
                onChange={(e) => setQuizConfig({ ...quizConfig, durationMinutes: parseInt(e.target.value, 10) || 0 })} />
              <TextInput label={t('training.quizQuestionCount')} type="number" value={quizConfig.questionCount}
                onChange={(e) => setQuizConfig({ ...quizConfig, questionCount: parseInt(e.target.value, 10) || 1 })} />
              <TextInput label={t('training.quizPassingScore')} type="number" value={quizConfig.passingScore}
                onChange={(e) => setQuizConfig({ ...quizConfig, passingScore: parseInt(e.target.value, 10) || 0 })} />
              <TextInput label={t('training.quizMaxAttempts')} type="number" value={quizConfig.maxAttempts}
                onChange={(e) => setQuizConfig({ ...quizConfig, maxAttempts: parseInt(e.target.value, 10) || 0 })} />
            </FormGrid>
            <FormGrid style={{ marginTop: 12 }}>
              <CheckboxInput label={t('training.quizShuffleQuestions')} checked={quizConfig.shuffleQuestions}
                onChange={(v) => setQuizConfig({ ...quizConfig, shuffleQuestions: v })} />
              <CheckboxInput label={t('training.quizShuffleOptions')} checked={quizConfig.shuffleOptions}
                onChange={(v) => setQuizConfig({ ...quizConfig, shuffleOptions: v })} />
              <CheckboxInput label={t('training.quizShowAnswers')} checked={quizConfig.showAnswersAfter}
                onChange={(v) => setQuizConfig({ ...quizConfig, showAnswersAfter: v })} />
              <CheckboxInput label={t('training.quizAllowBacktrack')} checked={quizConfig.allowBacktrack}
                onChange={(v) => setQuizConfig({ ...quizConfig, allowBacktrack: v })} />
              <CheckboxInput label={t('training.quizAntiCheat')} checked={quizConfig.antiCheatEnabled}
                onChange={(v) => setQuizConfig({ ...quizConfig, antiCheatEnabled: v })} />
            </FormGrid>

            <div style={{ marginTop: 24, borderTop: '1px solid #E0E4E8', paddingTop: 16 }}>
              <SectionTitle style={{ fontSize: 15 }}>{t('training.questionBank')}</SectionTitle>

              {questions.map((q, i) => (
                <QuestionItem key={q.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <QuestionText>{i + 1}. {q.questionText}</QuestionText>
                    <IconButton onClick={() => handleDeleteQuestion(q.id)}><Trash2 size={13} /></IconButton>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                    {t(`training.questionType_${q.questionType}`)} · {t(`training.difficulty_${q.difficulty}`)}
                  </div>
                  {q.options.map((o) => (
                    <OptionRow key={o.id} $correct={o.isCorrect}>
                      {o.isCorrect ? '✓' : '·'} {o.optionText}
                    </OptionRow>
                  ))}
                </QuestionItem>
              ))}

              <QuestionDraft
                question={newQuestion}
                onChange={setNewQuestion}
                onAdd={addQuestion}
              />
            </div>

            <Footer>
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft size={14} style={{ marginRight: 4 }} /> {t('common.back')}
              </Button>
              <Button onClick={handleNextFrom3}>
                {t('common.next')} <ArrowRight size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Footer>
          </>
        )}

        {step === 4 && (
          <>
            <SectionTitle>{t('training.wizardStep4')}</SectionTitle>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              {t('training.approverPickerHint')}
            </div>

            <AsyncGenericSelect
              label={t('training.approvers')}
              value={approvers}
              onChange={handleApproversChange}
              onSearch={searchEmployees}
              isMulti
            />

            <div style={{ marginTop: 16 }}>
              {approvers.map((id, i) => {
                const info = approverMap[id];
                return (
                  <ApproverRow key={id}>
                    <ApproverIndex>{i + 1}.</ApproverIndex>
                    {info?.profilePicture ? (
                      <img src={info.profilePicture} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#3730A3' }}>
                        {(info?.name || '?')[0]}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{info?.name || id}</div>
                      {info?.titleName && <div style={{ fontSize: 11, color: '#64748b' }}>{info.titleName}</div>}
                    </div>
                  </ApproverRow>
                );
              })}
            </div>

            <Footer>
              <Button variant="outline" onClick={() => setStep(form.hasQuiz ? 3 : 2)}>
                <ArrowLeft size={14} style={{ marginRight: 4 }} /> {t('common.back')}
              </Button>
              {!readOnly && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="outline" onClick={handleSaveDraftOnly} disabled={saving}>
                    {t('training.saveAsDraft')}
                  </Button>
                  <Button onClick={handleSubmit} disabled={!canSubmit || saving}>
                    <Check size={14} style={{ marginRight: 4 }} />
                    {saving ? '...' : t('training.submitForApproval')}
                  </Button>
                </div>
              )}
            </Footer>
          </>
        )}
      </Card>
    </Container>
  );
}

// --- Helpers ---

function emptyQuestion() {
  return {
    questionText: '',
    questionType: 'single_choice',
    difficulty: 'medium',
    category: '',
    points: 1,
    orderIndex: 0,
    options: [
      { optionText: '', isCorrect: true, orderIndex: 0 },
      { optionText: '', isCorrect: false, orderIndex: 1 },
    ],
  };
}

function normaliseQuestion(q) {
  return {
    ...q,
    options: q.options.filter((o) => o.optionText.trim().length > 0),
  };
}

function QuestionDraft({ question, onChange, onAdd }) {
  const { t } = useTranslation();

  const setOption = (idx, patch) => {
    const next = [...question.options];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...question, options: next });
  };

  const addOption = () => {
    onChange({
      ...question,
      options: [...question.options, { optionText: '', isCorrect: false, orderIndex: question.options.length }],
    });
  };

  const removeOption = (idx) => {
    onChange({ ...question, options: question.options.filter((_, i) => i !== idx) });
  };

  return (
    <div style={{ border: '1px dashed #94A3B8', borderRadius: 6, padding: 14, marginTop: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: '#004242' }}>{t('training.newQuestion')}</div>
      <TextInput
        label={t('training.questionText')}
        value={question.questionText}
        onChange={(e) => onChange({ ...question, questionText: e.target.value })}
        textarea
        rows={2}
      />
      <FormGrid style={{ marginTop: 8 }}>
        <GenericSelect
          label={t('training.questionType')}
          value={question.questionType}
          onChange={(v) => onChange({ ...question, questionType: v })}
          options={[
            { id: 'single_choice', name: t('training.questionType_single_choice') },
            { id: 'multiple_choice', name: t('training.questionType_multiple_choice') },
            { id: 'true_false', name: t('training.questionType_true_false') },
          ]}
          idKey="id"
          labelKey="name"
        />
        <GenericSelect
          label={t('training.questionDifficulty')}
          value={question.difficulty}
          onChange={(v) => onChange({ ...question, difficulty: v })}
          options={[
            { id: 'easy', name: t('training.difficulty_easy') },
            { id: 'medium', name: t('training.difficulty_medium') },
            { id: 'hard', name: t('training.difficulty_hard') },
          ]}
          idKey="id"
          labelKey="name"
        />
        <TextInput
          label={t('training.questionPoints')}
          type="number"
          value={question.points}
          onChange={(e) => onChange({ ...question, points: parseInt(e.target.value, 10) || 1 })}
        />
      </FormGrid>

      <div style={{ marginTop: 12 }}>
        {question.options.map((opt, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <CheckboxInput checked={opt.isCorrect} onChange={(v) => {
              if (question.questionType !== 'multiple_choice') {
                // Radio-style: only one can be correct.
                const next = question.options.map((o, i) => ({ ...o, isCorrect: i === idx ? v : false }));
                onChange({ ...question, options: next });
              } else {
                setOption(idx, { isCorrect: v });
              }
            }} />
            <TextInput
              value={opt.optionText}
              onChange={(e) => setOption(idx, { optionText: e.target.value })}
              placeholder={t('training.optionPlaceholder')}
              style={{ flex: 1 }}
            />
            {question.options.length > 2 && (
              <IconButton onClick={() => removeOption(idx)}><Trash2 size={13} /></IconButton>
            )}
          </div>
        ))}
        {question.questionType !== 'true_false' && question.options.length < 8 && (
          <Button variant="outline" onClick={addOption} style={{ marginTop: 4 }}>
            <Plus size={12} style={{ marginRight: 4 }} /> {t('training.addOption')}
          </Button>
        )}
      </div>

      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Button onClick={onAdd} disabled={!question.questionText.trim() || question.options.filter((o) => o.isCorrect).length === 0}>
          <Plus size={12} style={{ marginRight: 4 }} /> {t('training.addToQuestionBank')}
        </Button>
      </div>
    </div>
  );
}
